// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasCartLineContext, withCartLine } from "@insite/client-framework/Components/CartLineContext";
import { Cart } from "@insite/client-framework/Services/CartService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { PromotionModel } from "@insite/client-framework/Types/ApiModels";
import ProductAvailability from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import CartLineNotes, { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import CartLineQuantity, { CartLineQuantityStyles } from "@insite/content-library/Widgets/Cart/CartLineQuantity";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import HoverComponent from "@rugby/App/Components/HoverComponent/HoverComponent";
import ProductPrice, { ProductPriceStyles } from "@rugby/Overrides/Components/ProductPrice";
import cartLineStyles from "@rugby/Overrides/Widgets/Cart/Cartline.style";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: Cart;
    promotions?: PromotionModel[];
    showSavingsAmount: boolean;
    showSavingsPercent: boolean;
    editable: boolean;
    showInvalidPriceMessage?: boolean;
    extendedStyles?: CartLineCardExpandedStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
    isCheckoutReviewAndSubmitPage: getCurrentPage(state).type === "CheckoutReviewAndSubmitPage",
});

type Props = OwnProps & HasCartLineContext & ReturnType<typeof mapStateToProps>;

export interface CartLineCardExpandedStyles {
    cartLineNotes?: CartLineNotesStyles;
    price?: ProductPriceStyles;
    quantity?: CartLineQuantityStyles;
    container?: GridContainerProps;
    productImageGridItem?: GridItemProps;
    productImage?: ProductImageStyles;
    orderLineInfoGridItem?: GridItemProps;
    orderLineInfoContainer?: GridContainerProps;
    productDescriptionAndPartNumbersGridItem?: GridItemProps;
    productDescriptionAndPartNumbersContainer?: GridContainerProps;
    productBrand?: ProductBrandStyles;
    productBrandAndDescriptionGridItem?: GridItemProps;
    productDescription?: ProductDescriptionStyles;
    configurationGridItem?: GridItemProps;
    configurationOptionText?: TypographyProps;
    productPartNumbersGridItem?: GridItemProps;
    productPartNumbers?: ProductPartNumbersStyles;
    orderLineNotesGridItem?: GridItemProps;
    notesHeadingAndText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeValueText?: TypographyPresentationProps;
    costCodeLabelText?: TypographyPresentationProps;
    productPriceAndQuantityGridItem?: GridItemProps;
    productPriceAndQuantityContainer?: GridContainerProps;
    priceGridItem?: GridItemProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionNameText?: TypographyProps;
    quantityGridItem?: GridItemProps;
    qtyHeadingAndText?: SmallHeadingAndTextStyles;
    extendedUnitNetPriceGridItem?: GridItemProps;
    extendedUnitNetPrice?: SmallHeadingAndTextStyles;
    subtotalWithoutVatText?: SmallHeadingAndTextStyles;
    vatAmount?: SmallHeadingAndTextStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    cartLineErrorMessageText?: TypographyProps;
}

export const cartLineCardExpandedStyles: CartLineCardExpandedStyles = {
    container: {
        gap: 20,
        css: css`
            border-bottom: 1px solid ${getColor("common.border")};
            padding: 1rem 0;
        `,
    },
    productImageGridItem: {
        width: 2,
        printWidth: 1,
        css: css`
            @media print {
                max-height: 83px;
                max-width: 83px;
                padding: 0;
            }
        `,
    },
    orderLineInfoGridItem: {
        width: 10,
        printWidth: 11,
    },
    orderLineInfoContainer: {
        gap: 15,
    },
    productDescriptionAndPartNumbersGridItem: {
        width: [12, 12, 6, 6, 6],
        printWidth: 6,
    },
    productDescriptionAndPartNumbersContainer: {
        gap: 15,
    },
    productBrand: {
        nameText: {
            color: "text.main",
        },
    },
    productBrandAndDescriptionGridItem: {
        css: css`
            flex-direction: column;
        `,
        width: 12,
    },
    configurationGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    productPartNumbersGridItem: {
        width: 12,
    },
    orderLineNotesGridItem: {
        width: 12,
    },
    productPriceAndQuantityGridItem: {
        width: [12, 12, 6, 6, 6],
        printWidth: 6,
    },
    productPriceAndQuantityContainer: {
        gap: 15,
    },
    priceGridItem: {
        width: [12, 5, 12, 5, 5],
        printWidth: 5,
        css: css`
            flex-direction: column;
        `,
    },
    price: {
        price: {
            priceText: {
                weight: "normal",
            },
        },
    },
    quantityGridItem: {
        width: 3,
    },
    extendedUnitNetPriceGridItem: {
        width: 4,
    },
    extendedUnitNetPrice: {
        text: {
            weight: 700,
        },
    },
    cartLineErrorMessageGridItem: {
        width: 12,
    },
    cartLineErrorMessageText: {
        color: "danger",
    },
};

const CartLineCardExpanded = ({
    cart,
    cartLine,
    showSavingsAmount,
    showSavingsPercent,
    promotions,
    editable,
    enableVat,
    vatPriceDisplay,
    showInvalidPriceMessage,
    showPricing,
    isCheckoutReviewAndSubmitPage,
    extendedStyles,
}: Props) => {
    const styles = cartLineStyles;
    const [isHover, setIsHover] = useState(false);

    return (
        <HoverComponent
            {...styles.mainContainer}
            action={isHover => {
                setIsHover(isHover);
            }}
            timerOut={450}
            className={isHover ? "hover-mode" : ""}
            data-test-selector={`cartline_expanded_${cartLine.productId}_${cartLine.unitOfMeasure}`}
        >
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.productInfoContainer}>
                    <StyledWrapper {...styles.productImageContainer}>
                        <ProductImage product={cartLine} extendedStyles={styles.productImageStyles} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productBrandAndDescriptionContainer}>
                        <ProductDescription product={cartLine} extendedStyles={styles.productDescription} />
                        {cartLine.brand && <ProductBrand brand={cartLine.brand} extendedStyles={styles.productBrand} />}

                        <StyledWrapper {...styles.flexColumn}>
                            <StyledWrapper {...styles.productProductNumberAndAvailabilityContainer}>
                                <ProductPartNumbers
                                    productNumber={cartLine.erpNumber}
                                    customerProductNumber={cartLine.customerName}
                                    manufacturerItem={cartLine.manufacturerItem}
                                    extendedStyles={styles.productPartNumbersStyles}
                                />
                                {!cartLine.quoteRequired && (
                                    <StyledWrapper>
                                        <ProductAvailability
                                            productId={cartLine.productId!}
                                            availability={cartLine.availability!}
                                            unitOfMeasure={cartLine.unitOfMeasure}
                                            trackInventory={cartLine.trackInventory}
                                            extendedStyles={styles.productAvailability}
                                        />
                                    </StyledWrapper>
                                )}
                                {!cartLine.isFixedConfiguration && cartLine.sectionOptions!.length > 0 && (
                                    <StyledWrapper>
                                        {cartLine.sectionOptions!.map(option => (
                                            <Typography
                                                {...styles.configurationOptionText}
                                                key={option.sectionOptionId}
                                            >
                                                {`${option.sectionName}:${option.optionName}`}
                                            </Typography>
                                        ))}
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                        {cartLine.isRestricted && (
                            <StyledWrapper {...styles.flexColumn}>
                                <Typography
                                    {...styles.cartLineErrorMessageText}
                                    data-test-selector="cartline_restrictedMessage"
                                >
                                    {translate("Restricted product")}
                                </Typography>
                            </StyledWrapper>
                        )}
                        {!cartLine.isActive && (
                            <StyledWrapper {...styles.flexColumn}>
                                <Typography {...styles.cartLineErrorMessageText}>
                                    {translate("Inactive Product")}
                                </Typography>
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                <StyledWrapper {...styles.productInfoRightContainer}>
                    <StyledWrapper {...styles.priceContainer}>
                        {!cart.cartNotPriced && (
                            <ProductPrice
                                showLabel={true}
                                product={cartLine}
                                showSavings={true}
                                showPriceUOM={true}
                                showSavingsAmount={showSavingsAmount}
                                showSavingsPercent={showSavingsPercent}
                                showInvalidPriceMessage={showInvalidPriceMessage}
                                extendedStyles={styles.productPriceUOM}
                            />
                        )}
                        {promotions?.map(promotion => (
                            <Typography
                                {...styles.promotionNameText}
                                key={promotion.id}
                                data-test-selector="orderLineCardExpandedPromotionName"
                            >
                                {promotion.name}
                            </Typography>
                        ))}
                    </StyledWrapper>
                    <StyledWrapper
                        {...styles.quantityAndRemoveContainer}
                        data-test-selector="orderConformation_Quantity"
                    >
                        <CartLineQuantity cart={cart} editable={editable} extendedStyles={styles.quantity} />
                    </StyledWrapper>
                    <StyledWrapper {...styles.priceContainer}>
                        {!cart.cartNotPriced && (
                            <ProductPrice
                                showLabel={true}
                                product={cartLine}
                                showSavings={true}
                                showSavingsAmount={showSavingsAmount}
                                showSavingsPercent={showSavingsPercent}
                                extendedStyles={styles.productPrice}
                            />
                        )}
                        {promotions?.map(promotion => (
                            <Typography
                                {...styles.promotionNameText}
                                key={promotion.id}
                                data-test-selector="orderLineCardExpandedPromotionName"
                            >
                                {promotion.name}
                            </Typography>
                        ))}
                    </StyledWrapper>
                    {cartLine.pricing && (showPricing || isCheckoutReviewAndSubmitPage) && (
                        <StyledWrapper {...styles.extendedUnitNetPriceContainer}>
                            <Typography variant="legend" {...styles.totalLabelText}>
                                {!enableVat
                                    ? translate("Subtotal")
                                    : vatPriceDisplay !== "DisplayWithoutVat"
                                    ? `${translate("Subtotal")} (${translate("Inc. VAT")})`
                                    : `${translate("Subtotal")} (${translate("Ex. VAT")})`}
                            </Typography>
                            <Typography
                                {...styles.extendedUnitNetPriceText}
                                data-test-selector="cartline_extendedUnitNetPrice"
                            >
                                {enableVat && vatPriceDisplay !== "DisplayWithoutVat"
                                    ? cartLine.pricing.extendedUnitRegularPriceWithVatDisplay
                                    : cartLine.pricing.extendedUnitNetPriceDisplay}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.cartLineActions} className="show-on-hover cartLine-actions">
                {cartLine.notes && (
                    <CartLineNotes
                        cart={cart}
                        editable={editable}
                        label={translate("Line Notes")}
                        extendedStyles={styles.cartLineNotes}
                    />
                )}
            </StyledWrapper>
        </HoverComponent>
    );
};

export default connect(mapStateToProps)(withCartLine(CartLineCardExpanded));
